import { Ref, ref, ExtractPropTypes, toRefs, computed, watch, nextTick, SetupContext } from '@vue/composition-api';

import { CREATIVE_EVENT_NAME, AthenaBotIframeMsgAction } from './types';
import Props from './props';

export const useIframe = (props: ExtractPropTypes<typeof Props>, { emit }: SetupContext) => {
  const {
    iframeUrl,
    visible,
    getInitData,
    initData,
    lang,
    extEventNameList,
    width,
    isAthenaCallerReady,
    freeCloseBeforeLoaded,
  } = toRefs(props);
  const [isInit, loading, curInitData, curWidth] = [
    ref(false),
    ref(false),
    ref<Record<string, unknown>>({}),
    ref(width?.value || '80%'),
  ];
  const iframe: Ref<HTMLIFrameElement | null> = ref(null);
  const iframeId = computed(() => curInitData.value?.id || '');

  const isInstantPageIframe = computed(() => iframeUrl.value?.includes('instant_page'));
  const eventNameList = computed(() => {
    const preList = extEventNameList.value.concat(Object.values(CREATIVE_EVENT_NAME));
    return isInstantPageIframe.value ? preList.concat([CREATIVE_EVENT_NAME.ATHENA_BOT_IFRAME_MSG_TYPE]) : preList;
  });

  const sendMessage = <T>(type: CREATIVE_EVENT_NAME, data: T) => {
    // 如果抽屉已经被操作关闭了，不再发送消息。
    if (!iframe.value || !visible.value) {
      return;
    }
    iframe?.value?.contentWindow?.postMessage({ type, data }, '*');
  };

  const initIframe = async () => {
    if (!freeCloseBeforeLoaded) {
      loading.value = false;
    }
    if (!isInit.value) {
      const data = typeof getInitData.value === 'function' ? await getInitData.value() : initData.value;
      sendMessage(CREATIVE_EVENT_NAME.INIT, data);
      isInit.value = true;
      curInitData.value = data;
    }
  };

  const handleReceiveAthenaBotIframeMsg = (data: { action: string; keyword?: string }) => {
    if (data?.action === AthenaBotIframeMsgAction.SEND_KEYWORD && data?.keyword) {
      (window as any).intelliCS &&
        (window as any).intelliCS.openDialog({
          question: {
            type: 'best_practice',
            keyword: data.keyword,
          },
        });
    }
  };

  const handleEvent = (event: MessageEvent) => {
    const SUCCESS_CODE = 0;
    if (
      (event?.data?.code === SUCCESS_CODE && event?.data?.id === iframeId.value) ||
      event?.source === iframe.value?.contentWindow
    ) {
      const resp = event.data;
      const { data = {} } = resp;
      const eventName = eventNameList.value.find(i => i === resp?.type || i === data?.type);
      if (eventName) {
        if (eventName === CREATIVE_EVENT_NAME.LOADED) {
          loading.value = false;
        }
        eventName === CREATIVE_EVENT_NAME.LOADED && (loading.value = false);
        eventName === CREATIVE_EVENT_NAME.INIT && initIframe();
        eventName === CREATIVE_EVENT_NAME.RESIZE && (curWidth.value = data?.width || curWidth.value);
        emit(eventName, data?.data || data);

        if (eventName === CREATIVE_EVENT_NAME.ATHENA_BOT_IFRAME_MSG_TYPE) {
          handleReceiveAthenaBotIframeMsg(data);
        }
      }
    }
  };

  const messageHandler = (event: MessageEvent) => {
    const hasEvent = eventNameList.value?.length > 0;
    if (!hasEvent) {
      return;
    }
    handleEvent(event);
  };

  const onClose = () => {
    console.log('close');
    if (loading.value) {
      // 如果抽屉iframe在加载中 直接关闭，防止iframe在加载完毕之前无法关闭抽屉
      emit('update:visible', visible.value);
      emit('close');
    } else {
      sendMessage(CREATIVE_EVENT_NAME.CLOSE, {});
      // 如果抽屉加载完毕，则关闭行为由抽屉内部接管
      emit('close-confirm');
    }
  };

  // useLeadFormAthenaBot，写在组件内部，是因为外界leadads iframe 弹窗较多，后续扩展比较还需额外开发，这里在源头截取和处理逻辑

  // 以下新增了一些 instant form 独有的逻辑，一开始是放在组件外，但是应用场景较多，就把逻辑放在里头

  const iframeInitDoneAndAthenaBotReady = computed(() => isAthenaCallerReady.value && isInit.value);

  watch(iframeInitDoneAndAthenaBotReady, newVal => {
    if (newVal) {
      sendMessage(CREATIVE_EVENT_NAME.ATHENA_BOT_IFRAME_MSG_TYPE, {
        action: AthenaBotIframeMsgAction.BOT_READY,
      });

      if ((window as any).intelliCS) {
        // 注意这里的智能客服updateConfig 是直接覆盖之前的 onOpenDialog，之前的 onOpenDialog event 不再触发，如果后续需要加监听格外注意
        console.log('window.intelliCS.updateConfig 在 creative-iframe-drawer 中触发了，覆盖了之前的配置');
        (window as any).intelliCS.updateConfig({
          onOpenDialog: () => {
            sendMessage(CREATIVE_EVENT_NAME.ATHENA_BOT_IFRAME_MSG_TYPE, {
              action: AthenaBotIframeMsgAction.OPEN_BOT,
            });
          },
        });
      }
    }
  });
  // useLeadFormAthenaBot end

  watch(
    () => lang.value,
    val => sendMessage(CREATIVE_EVENT_NAME.CHANGE_LANG, val),
  );
  watch(
    () => visible.value,
    async val => {
      if (val) {
        loading.value = true;
        await nextTick();
        if (iframe.value) {
          iframe.value.onload = initIframe;
          window.addEventListener('message', messageHandler);
        }
      } else {
        isInit.value = false;
        window.removeEventListener('message', messageHandler);
      }
    },
  );

  watch(
    isInit,
    newVal => {
      emit('init-done-change', newVal);
    },
    { immediate: true },
  );

  return {
    iframe,
    sendMessage,
    initIframe,
    onClose,
    isInit,
    loading,
    curInitData,
    iframeId,
    curWidth,
  };
};
